import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  fetchAllOkrs,
  resetOkr,
  quarterLabel,
  quarterOptions,
  currentQuarter,
  type OkrOwner,
  type OkrTask,
} from '../../api/okrs';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { DataState } from '../../components/DataState';
import { KeyResultProgress, LinkedTaskRow, OkrStatusChip, TaskList, taskDueLabel } from './OkrShared';

interface LinkedToOwner {
  task: OkrTask;
  from: OkrOwner | null;
  krLabel: string;
  due: string;
}

export function PeopleOkrPage() {
  const [quarter, setQuarter] = useState(currentQuarter());
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirmResetId, setConfirmResetId] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const focusUserId = searchParams.get('user');
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const focusedRef = useRef<string | null>(null);

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const query = useQuery({
    queryKey: ['all-okrs', quarter],
    queryFn: ({ signal }) => fetchAllOkrs(quarter, signal).then((r) => r.okrs),
    refetchOnWindowFocus: false,
  });

  const resetMutation = useMutation({
    mutationFn: (id: string) => resetOkr(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-okrs', quarter] });
      setConfirmResetId(null);
    },
  });

  // When arriving via an @Name link (?user=<id>), expand that person's card and
  // scroll it into view once — but only the first time we see this id/dataset.
  useEffect(() => {
    if (!focusUserId || !query.data) return;
    if (focusedRef.current === focusUserId) return;
    const entry = query.data.find((e) => e.owner?.id === focusUserId);
    if (!entry) return;
    focusedRef.current = focusUserId;
    setExpanded((prev) => new Set(prev).add(entry.okr.id));
    const el = cardRefs.current.get(entry.okr.id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusUserId, query.data]);

  const options = quarterOptions();

  // Tasks each person had linked to them by someone else, keyed by that person's
  // id. Sourced from the same /api/okrs/all payload — every task already carries
  // its linkedUser — so a linked task shows under the assignee's card too, not
  // only the author's. Read-only here; status stays owned by the author.
  const linkedByOwner = useMemo(() => {
    const map = new Map<string, LinkedToOwner[]>();
    for (const entry of query.data ?? []) {
      for (const kr of entry.okr.keyResults) {
        for (const task of kr.tasks) {
          const linkedId = task.linkedUser?.id;
          if (!linkedId) continue;
          const list = map.get(linkedId) ?? [];
          list.push({ task, from: entry.owner, krLabel: kr.label, due: taskDueLabel(kr, task) });
          map.set(linkedId, list);
        }
      }
    }
    return map;
  }, [query.data]);

  const filtered = (query.data ?? []).filter((entry) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (entry.owner?.name ?? '').toLowerCase().includes(q) ||
      (entry.owner?.email ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      <PageHeader
        title="Everyone's OKRs"
        subtitle="Browse quarterly OKRs across the whole team — read-only view."
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Quarter</InputLabel>
              <Select
                label="Quarter"
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
              >
                {options.map((q) => (
                  <MenuItem key={q} value={q}>
                    {quarterLabel(q)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        }
      />

      <DataState
        loading={query.isLoading}
        error={query.error}
        data={query.data}
        isEmpty={(d) => d.length === 0}
        emptyMessage="No OKRs have been submitted for this quarter yet."
      >
        {() => (
          <Stack spacing={2}>
            {filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No results match your search.
              </Typography>
            ) : (
              filtered.map((entry) => {
                const isOpen = expanded.has(entry.okr.id);
                return (
                  <Card
                    key={entry.okr.id}
                    ref={(el: HTMLDivElement | null) => {
                      if (el) cardRefs.current.set(entry.okr.id, el);
                      else cardRefs.current.delete(entry.okr.id);
                    }}
                    sx={{ borderRadius: 3 }}
                  >
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ sm: 'center' }}
                        gap={1.5}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={() => toggle(entry.okr.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle(entry.okr.id);
                          }
                        }}
                        sx={{ mb: isOpen ? 2 : 0, cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar src={entry.owner?.picture ?? undefined} sx={{ width: 36, height: 36 }}>
                            {entry.owner?.name?.charAt(0) ?? '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {entry.owner?.name ?? 'Unknown user'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.owner?.email}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {quarterLabel(entry.okr.quarter)}
                          </Typography>
                          <OkrStatusChip status={entry.okr.status} />
                          {isAdmin && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={resetMutation.isPending && resetMutation.variables === entry.okr.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmResetId(entry.okr.id);
                              }}
                            >
                              Reset to draft
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            tabIndex={-1}
                            aria-hidden
                            sx={{ pointerEvents: 'none' }}
                          >
                            <ExpandMoreRoundedIcon
                              fontSize="small"
                              sx={{
                                transition: 'transform 150ms',
                                transform: isOpen ? 'rotate(180deg)' : 'none',
                                color: 'text.secondary',
                              }}
                            />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Collapse in={isOpen} unmountOnExit>
                        <Stack spacing={2}>
                          {entry.okr.keyResults.map((kr, i) => (
                            <Box key={`${entry.okr.id}-${i}`}>
                              <KeyResultProgress kr={kr} />
                              <TaskList kr={kr} />
                            </Box>
                          ))}
                          {(() => {
                            const linked = entry.owner ? linkedByOwner.get(entry.owner.id) ?? [] : [];
                            if (linked.length === 0) return null;
                            return (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.07em',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    color: 'text.secondary',
                                  }}
                                >
                                  Linked to {entry.owner?.name ?? 'them'} · {linked.length}
                                </Typography>
                                <Stack divider={<Divider />} sx={{ mt: 0.25 }}>
                                  {linked.map(({ task, from, krLabel, due }) => (
                                    <LinkedTaskRow
                                      key={task.id}
                                      name={task.name}
                                      description={task.description}
                                      notionUrl={task.notionUrl}
                                      from={from}
                                      krLabel={krLabel}
                                      due={due}
                                      status={task.status}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            );
                          })()}
                        </Stack>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Stack>
        )}
      </DataState>

      <Dialog open={confirmResetId !== null} onClose={() => setConfirmResetId(null)}>
        <DialogTitle>Reset OKR to draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will discard the manager's approval and return the OKR to draft so the employee can
            revise and resubmit. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetId(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={resetMutation.isPending}
            onClick={() => {
              if (confirmResetId) resetMutation.mutate(confirmResetId);
            }}
          >
            Reset to draft
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
